/**
 * Virrelli Dining Table Size Calculator
 * ------------------------------------------------------------------
 * Self-contained custom element: <virrelli-table-calculator>
 *
 * - No external dependencies. Vanilla JavaScript only.
 * - All editorial sizing values live in VIRRELLI_CALC_CONFIG below.
 * - Clearance values are provided by the section settings via
 *   data-comfortable-clearance / data-minimum-clearance (mm) and fall
 *   back to the config defaults if absent.
 * - Product data is server-rendered by Liquid into [data-calc-product]
 *   elements; this script only reads their data attributes. No product
 *   names or URLs are hard-coded here.
 * - Safe to re-initialise inside the Shopify Theme Editor
 *   (shopify:section:load / custom element lifecycle).
 */
(function () {
  'use strict';

  if (customElements.get('virrelli-table-calculator')) return;

  /* ==================================================================
   * 1. CONFIGURATION - the ONLY place sizing guidance lives.
   *    All dimensions in millimetres.
   * ================================================================== */
  var VIRRELLI_CALC_CONFIG = {
    clearance: {
      comfortable: 1000, // preferred space around the table (mm)
      minimum: 900       // practical minimum guide (mm)
    },
    // Sensible room validation bounds (mm)
    room: {
      min: 1500,   // below this a dining table is not realistic
      max: 30000   // above this is almost certainly a typo
    },
    seats: {
      min: 2,
      max: 20
    },
    /**
     * Editorial table-size guidance per shape and seat count.
     * round:        { d: [minDiameter, maxDiameter] }
     * oval / rect:  { l: [minLength, maxLength], w: [minWidth, maxWidth] }
     */
    sizes: {
      round: {
        4:  { d: [1000, 1200] },
        6:  { d: [1200, 1400] },
        8:  { d: [1500, 1600] },
        10: { d: [1800, 2000] },
        12: { d: [2100, 2300] }
      },
      oval: {
        4:  { l: [1400, 1600], w: [850, 950] },
        6:  { l: [1600, 1800], w: [900, 1000] },
        8:  { l: [2000, 2200], w: [1000, 1100] },
        10: { l: [2400, 2600], w: [1000, 1100] },
        12: { l: [2800, 3000], w: [1100, 1200] }
      },
      rectangular: {
        4:  { l: [1200, 1400], w: [800, 900] },
        6:  { l: [1600, 1800], w: [900, 1000] },
        8:  { l: [2000, 2200], w: [1000, 1100] },
        10: { l: [2400, 2600], w: [1000, 1100] },
        12: { l: [2800, 3000], w: [1100, 1200] }
      }
    },
    shapeNotes: {
      round: 'A round table leaves more room to move around the space and suits square or evenly proportioned rooms.',
      oval: 'An oval table gives you additional seating without the visual weight of a full rectangular table.',
      rectangular: 'A rectangular table makes the most of the length of the room and creates a stronger, more architectural layout.'
    }
  };

  var SHAPES = ['round', 'oval', 'rectangular'];
  var FIT = { COMFORTABLE: 'comfortable', POSSIBLE: 'possible', BESPOKE: 'bespoke' };

  /* ==================================================================
   * 2. Pure calculation helpers
   * ================================================================== */

  /** Interpolate guidance for custom seat counts not in the table. */
  function guidanceFor(shape, seats) {
    var table = VIRRELLI_CALC_CONFIG.sizes[shape];
    if (table[seats]) return table[seats];
    var keys = Object.keys(table).map(Number).sort(function (a, b) { return a - b; });
    // Clamp outside the known range
    if (seats <= keys[0]) return table[keys[0]];
    if (seats >= keys[keys.length - 1]) return table[keys[keys.length - 1]];
    // Linear interpolation between the two nearest seat counts
    var lo, hi;
    for (var i = 0; i < keys.length - 1; i++) {
      if (seats > keys[i] && seats < keys[i + 1]) { lo = keys[i]; hi = keys[i + 1]; break; }
      if (seats === keys[i]) return table[keys[i]];
    }
    var t = (seats - lo) / (hi - lo);
    function lerpPair(a, b) {
      return [Math.round(a[0] + (b[0] - a[0]) * t), Math.round(a[1] + (b[1] - a[1]) * t)];
    }
    var g = {};
    if (table[lo].d) g.d = lerpPair(table[lo].d, table[hi].d);
    if (table[lo].l) g.l = lerpPair(table[lo].l, table[hi].l);
    if (table[lo].w) g.w = lerpPair(table[lo].w, table[hi].w);
    return g;
  }

  /**
   * Evaluate one shape against the room.
   * Returns { shape, fit, size, clearances, orientation }
   * roomL / roomW in mm; clearances in mm.
   */
  function evaluateShape(shape, seats, roomL, roomW, comfy, minimum) {
    var g = guidanceFor(shape, seats);

    if (shape === 'round') {
      var smallest = Math.min(roomL, roomW);
      var maxDiaComfy = smallest - 2 * comfy;
      var maxDiaMin = smallest - 2 * minimum;
      var lo = g.d[0], hi = g.d[1];

      if (maxDiaComfy >= lo) {
        var dia = Math.min(hi, maxDiaComfy);
        return {
          shape: shape,
          fit: FIT.COMFORTABLE,
          size: { d: [lo, dia] },
          clearances: { around: Math.round((smallest - dia) / 2) },
          margin: maxDiaComfy - lo
        };
      }
      if (maxDiaMin >= lo) {
        var dia2 = Math.min(hi, maxDiaMin);
        return {
          shape: shape,
          fit: FIT.POSSIBLE,
          size: { d: [lo, dia2] },
          clearances: { around: Math.round((smallest - dia2) / 2) },
          margin: maxDiaMin - lo
        };
      }
      return { shape: shape, fit: FIT.BESPOKE, size: { d: g.d }, clearances: null, margin: maxDiaMin - lo };
    }

    // Oval / rectangular: test both orientations, keep the better one.
    var best = null;
    var orientations = [
      { along: 'length', L: roomL, W: roomW },
      { along: 'width',  L: roomW, W: roomL }
    ];
    orientations.forEach(function (o) {
      var maxLenComfy = o.L - 2 * comfy;
      var maxWidComfy = o.W - 2 * comfy;
      var maxLenMin = o.L - 2 * minimum;
      var maxWidMin = o.W - 2 * minimum;
      var res;

      if (maxLenComfy >= g.l[0] && maxWidComfy >= g.w[0]) {
        var len = Math.min(g.l[1], maxLenComfy);
        var wid = Math.min(g.w[1], maxWidComfy);
        res = {
          fit: FIT.COMFORTABLE,
          size: { l: [g.l[0], len], w: [g.w[0], wid] },
          clearances: { ends: Math.round((o.L - len) / 2), sides: Math.round((o.W - wid) / 2) },
          margin: Math.min(maxLenComfy - g.l[0], maxWidComfy - g.w[0])
        };
      } else if (maxLenMin >= g.l[0] && maxWidMin >= g.w[0]) {
        var len2 = Math.min(g.l[1], maxLenMin);
        var wid2 = Math.min(g.w[1], maxWidMin);
        res = {
          fit: FIT.POSSIBLE,
          size: { l: [g.l[0], len2], w: [g.w[0], wid2] },
          clearances: { ends: Math.round((o.L - len2) / 2), sides: Math.round((o.W - wid2) / 2) },
          margin: Math.min(maxLenMin - g.l[0], maxWidMin - g.w[0])
        };
      } else {
        res = {
          fit: FIT.BESPOKE,
          size: { l: g.l, w: g.w },
          clearances: null,
          margin: Math.min(maxLenMin - g.l[0], maxWidMin - g.w[0])
        };
      }
      res.shape = shape;
      res.orientation = o.along;
      if (!best || fitRank(res.fit) < fitRank(best.fit) ||
          (fitRank(res.fit) === fitRank(best.fit) && res.margin > best.margin)) {
        best = res;
      }
    });
    return best;
  }

  function fitRank(fit) {
    return fit === FIT.COMFORTABLE ? 0 : fit === FIT.POSSIBLE ? 1 : 2;
  }

  /**
   * Editorial shape preference by room proportion, used only to order
   * shapes that achieve the SAME fit status. Round suits square or evenly
   * proportioned rooms; oval and rectangular make better use of longer rooms.
   * Lower is better.
   */
  function shapeAffinity(shape, roomL, roomW) {
    var ratio = Math.max(roomL, roomW) / Math.min(roomL, roomW);
    if (ratio <= 1.35) {
      return { round: 0, oval: 1, rectangular: 2 }[shape];
    }
    return { oval: 0, rectangular: 1, round: 2 }[shape];
  }

  /** mm -> "120cm" or "120–140cm" for a [lo,hi] pair */
  function cm(mm) { return Math.round(mm / 10); }
  function cmRange(pair) {
    var a = cm(pair[0]), b = cm(pair[1]);
    return a === b ? a + 'cm' : a + '\u2013' + b + 'cm';
  }

  function shapeLabel(shape) {
    return shape.charAt(0).toUpperCase() + shape.slice(1);
  }

  function sizeText(result) {
    if (result.size.d) return cmRange(result.size.d) + ' diameter';
    return cmRange(result.size.l) + ' \u00d7 ' + cmRange(result.size.w);
  }

  /* ==================================================================
   * 3. Custom element
   * ================================================================== */
  function VirrelliTableCalculator() {
    return Reflect.construct(HTMLElement, [], VirrelliTableCalculator);
  }
  VirrelliTableCalculator.prototype = Object.create(HTMLElement.prototype);
  VirrelliTableCalculator.prototype.constructor = VirrelliTableCalculator;

  VirrelliTableCalculator.prototype.connectedCallback = function () {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';

    this.config = VIRRELLI_CALC_CONFIG;
    this.clearComfy = parseInt(this.dataset.comfortableClearance, 10) || this.config.clearance.comfortable;
    this.clearMin = parseInt(this.dataset.minimumClearance, 10) || this.config.clearance.minimum;
    if (this.clearMin > this.clearComfy) this.clearMin = this.clearComfy;

    this.state = { step: 1, roomL: null, roomW: null, seats: null, shape: 'open', started: false };

    this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-calc-step]'));
    this.resultRegion = this.querySelector('[data-calc-result]');
    this.products = this.readProducts();

    this.bindEvents();
    this.showStep(1);
  };

  VirrelliTableCalculator.prototype.readProducts = function () {
    return Array.prototype.slice.call(this.querySelectorAll('[data-calc-product]')).map(function (el, i) {
      return {
        el: el,
        order: i,
        shape: el.dataset.shape || '',
        diameter: parseInt(el.dataset.diameterMm, 10) || 0,
        length: parseInt(el.dataset.lengthMm, 10) || 0,
        width: parseInt(el.dataset.widthMm, 10) || 0,
        minSeats: parseInt(el.dataset.minSeats, 10) || 0,
        maxSeats: parseInt(el.dataset.maxSeats, 10) || 99,
        available: el.dataset.available !== 'false'
      };
    }).filter(function (p) {
      // Defensive: ignore blocks with no usable dimensions
      if (p.shape === 'round') return p.diameter > 0;
      return p.length > 0 && p.width > 0;
    });
  };

  VirrelliTableCalculator.prototype.bindEvents = function () {
    var self = this;

    this.addEventListener('click', function (e) {
      var next = e.target.closest('[data-calc-next]');
      var back = e.target.closest('[data-calc-back]');
      var restart = e.target.closest('[data-calc-restart]');
      if (next) { e.preventDefault(); self.next(); }
      if (back) { e.preventDefault(); self.showStep(self.state.step - 1); }
      if (restart) { e.preventDefault(); self.restart(); }
    });

    // Fire "start" once, on first interaction
    this.addEventListener('input', function () { self.markStarted(); }, { once: false });
    this.addEventListener('change', function () { self.markStarted(); });

    // Seats: reveal custom input when Custom is chosen
    var seatRadios = this.querySelectorAll('input[name="' + this.fieldName('seats') + '"]');
    Array.prototype.forEach.call(seatRadios, function (r) {
      r.addEventListener('change', function () {
        var customWrap = self.querySelector('[data-calc-custom-seats]');
        if (customWrap) {
          var isCustom = r.value === 'custom' && r.checked;
          customWrap.hidden = !isCustom;
          if (isCustom) customWrap.querySelector('input').focus();
        }
      });
    });

    // Product / bespoke click analytics (delegated; links are server-rendered)
    this.addEventListener('click', function (e) {
      var productLink = e.target.closest('[data-calc-product-link]');
      var bespokeLink = e.target.closest('[data-calc-bespoke-link]');
      if (productLink) {
        self.track('virrelli_table_calculator_product_click', { product_url: productLink.getAttribute('href') });
      }
      if (bespokeLink) {
        self.track('virrelli_table_calculator_bespoke_click', {});
      }
    });

    // Enquiry form: stamp calculator context into hidden field on submit
    var form = this.querySelector('[data-calc-enquiry] form');
    if (form) {
      form.addEventListener('submit', function () {
        var hidden = form.querySelector('[data-calc-context-field]');
        if (hidden) hidden.value = self.enquirySummary();
        self.track('virrelli_table_calculator_enquiry', {});
      });
    }
  };

  VirrelliTableCalculator.prototype.fieldName = function (base) {
    // Field names are suffixed with the section id in Liquid for uniqueness
    var input = this.querySelector('[data-calc-field="' + base + '"]');
    return input ? input.name : base;
  };

  VirrelliTableCalculator.prototype.markStarted = function () {
    if (this.state.started) return;
    this.state.started = true;
    this.track('virrelli_table_calculator_start', {});
  };

  /* ---------------- step navigation ---------------- */

  VirrelliTableCalculator.prototype.showStep = function (n) {
    if (n < 1) n = 1;
    this.state.step = n;
    this.steps.forEach(function (el) {
      var isCurrent = parseInt(el.dataset.calcStep, 10) === n;
      el.hidden = !isCurrent;
    });
    if (this.resultRegion) this.resultRegion.hidden = n !== 4;
    // Move focus to the step heading for keyboard/AT users on Back/Continue,
    // but never when the result merely re-renders (handled separately).
    var current = this.steps.filter(function (el) { return !el.hidden; })[0];
    if (current) {
      var h = current.querySelector('[data-calc-step-heading]');
      if (h && this.state.started) h.focus({ preventScroll: false });
    }
  };

  VirrelliTableCalculator.prototype.next = function () {
    var s = this.state;
    if (s.step === 1 && !this.validateRoom()) return;
    if (s.step === 2 && !this.validateSeats()) return;
    if (s.step === 3) {
      this.readShape();
      this.calculateAndRender();
      this.showStep(4);
      return;
    }
    this.showStep(s.step + 1);
  };

  VirrelliTableCalculator.prototype.restart = function () {
    this.showStep(1);
  };

  /* ---------------- validation ---------------- */

  VirrelliTableCalculator.prototype.validateRoom = function () {
    var lenInput = this.querySelector('[data-calc-field="room_length"]');
    var widInput = this.querySelector('[data-calc-field="room_width"]');
    var err = this.querySelector('[data-calc-error="room"]');
    var L = parseFloat(String(lenInput.value).replace(',', '.'));
    var W = parseFloat(String(widInput.value).replace(',', '.'));
    var minM = this.config.room.min / 1000;
    var maxM = this.config.room.max / 1000;

    var problems = [];
    if (isNaN(L) || L <= 0) problems.push('Please enter your room length in metres.');
    if (isNaN(W) || W <= 0) problems.push('Please enter your room width in metres.');
    if (!problems.length) {
      if (L < minM || W < minM) problems.push('Please check your measurements \u2013 rooms below ' + minM + 'm are too small for a dining table.');
      if (L > maxM || W > maxM) problems.push('Please check your measurements \u2013 dimensions above ' + maxM + 'm look like a typo.');
    }
    if (problems.length) {
      err.textContent = problems.join(' ');
      err.hidden = false;
      (isNaN(L) || L <= 0 ? lenInput : widInput).focus();
      return false;
    }
    err.hidden = true;
    this.state.roomL = Math.round(L * 1000); // convert to mm internally
    this.state.roomW = Math.round(W * 1000);
    return true;
  };

  VirrelliTableCalculator.prototype.validateSeats = function () {
    var checked = this.querySelector('input[name="' + this.fieldName('seats') + '"]:checked');
    var err = this.querySelector('[data-calc-error="seats"]');
    var seats = null;

    if (checked && checked.value === 'custom') {
      var custom = this.querySelector('[data-calc-field="custom_seats"]');
      seats = parseInt(custom && custom.value, 10);
    } else if (checked) {
      seats = parseInt(checked.value, 10);
    }

    if (!seats || seats < this.config.seats.min || seats > this.config.seats.max) {
      err.textContent = 'Please choose how many people you\u2019d like to seat (' + this.config.seats.min + '\u2013' + this.config.seats.max + ').';
      err.hidden = false;
      return false;
    }
    err.hidden = true;
    this.state.seats = seats;
    return true;
  };

  VirrelliTableCalculator.prototype.readShape = function () {
    var checked = this.querySelector('input[name="' + this.fieldName('shape') + '"]:checked');
    this.state.shape = checked ? checked.value : 'open';
  };

  /* ---------------- calculation + rendering ---------------- */

  VirrelliTableCalculator.prototype.calculateAndRender = function () {
    var s = this.state;
    var self = this;

    // Evaluate every shape so we can rank alternatives.
    var results = SHAPES.map(function (shape) {
      return evaluateShape(shape, s.seats, s.roomL, s.roomW, self.clearComfy, self.clearMin);
    });

    var rankOpen = function (a, b) {
      var f = fitRank(a.fit) - fitRank(b.fit);
      if (f !== 0) return f;
      var aff = shapeAffinity(a.shape, s.roomL, s.roomW) - shapeAffinity(b.shape, s.roomL, s.roomW);
      return aff !== 0 ? aff : b.margin - a.margin;
    };

    var ranked;
    if (s.shape === 'open') {
      ranked = results.slice().sort(rankOpen);
    } else {
      var preferred = results.filter(function (r) { return r.shape === s.shape; });
      var others = results.filter(function (r) { return r.shape !== s.shape; }).sort(rankOpen);
      ranked = preferred.concat(others);
    }

    var primary = ranked[0];
    var alternative = ranked.filter(function (r) { return r.fit !== FIT.BESPOKE; })[1] || null;

    this.lastResult = { primary: primary, alternative: alternative, all: results };
    this.renderResult(primary, alternative);
    this.renderProducts(primary);

    this.track('virrelli_table_calculator_complete', {
      room_length: s.roomL / 1000,
      room_width: s.roomW / 1000,
      seats: s.seats,
      preferred_shape: s.shape,
      recommended_shape: primary.shape,
      fit_status: primary.fit,
      recommended_size: sizeText(primary)
    });
  };

  VirrelliTableCalculator.prototype.renderResult = function (primary, alternative) {
    var s = this.state;
    var el = this.resultRegion;
    if (!el) return;

    var metres = function (mm) { return (mm / 1000).toFixed(mm % 1000 === 0 ? 0 : 1); };
    el.querySelector('[data-calc-out="room"]').textContent = metres(s.roomL) + 'm \u00d7 ' + metres(s.roomW) + 'm';
    el.querySelector('[data-calc-out="seats"]').textContent = s.seats + ' people';

    // Fit badge
    var badge = el.querySelector('[data-calc-out="fit"]');
    var fitText = { comfortable: 'Comfortable fit', possible: 'Possible fit', bespoke: 'Bespoke review recommended' };
    badge.textContent = fitText[primary.fit];
    badge.setAttribute('data-fit', primary.fit);

    // Natural-language recommendation
    var rec = el.querySelector('[data-calc-out="recommendation"]');
    var text = '';
    if (primary.fit === FIT.BESPOKE) {
      var g = sizeText(primary);
      text = 'A standard ' + primary.shape + ' table for ' + s.seats + ' people (around ' + g + ') would sit closer to the walls than we\u2019d usually suggest in this room. A bespoke table may be the best fit \u2013 we can adjust the size, stone and proportions around your space.';
    } else {
      text = 'A ' + sizeText(primary) + ' ' + primary.shape + ' dining table would work well in this room.';
      if (primary.size.d) {
        text += ' A table around ' + cm(primary.size.d[1]) + 'cm diameter would leave approximately ' + cm(primary.clearances.around) + 'cm around the table.';
      } else {
        text += ' Placed with its length along the room\u2019s ' + primary.orientation + ', it would leave approximately ' + cm(primary.clearances.ends) + 'cm at each end and ' + cm(primary.clearances.sides) + 'cm along each side.';
      }
      if (primary.fit === FIT.POSSIBLE) {
        text += ' This sits a little below our preferred 100cm of surrounding space, so we\u2019d recommend discussing the room layout with us.';
      }
    }
    rec.textContent = text;

    // Shape ranking (best fit / alternative)
    var shapesWrap = el.querySelector('[data-calc-out="shapes"]');
    shapesWrap.innerHTML = '';
    var self = this;
    var addShapeCard = function (label, result) {
      if (!result) return;
      var card = document.createElement('div');
      card.className = 'vcalc-shape-card';
      card.innerHTML =
        '<p class="vcalc-eyebrow"></p>' +
        '<p class="vcalc-shape-card__name"></p>' +
        '<p class="vcalc-shape-card__size"></p>' +
        '<p class="vcalc-shape-card__note"></p>';
      card.querySelector('.vcalc-eyebrow').textContent = label;
      card.querySelector('.vcalc-shape-card__name').textContent = shapeLabel(result.shape);
      card.querySelector('.vcalc-shape-card__size').textContent = sizeText(result);
      card.querySelector('.vcalc-shape-card__note').textContent = self.config.shapeNotes[result.shape];
      shapesWrap.appendChild(card);
    };
    if (primary.fit !== FIT.BESPOKE) {
      addShapeCard(this.state.shape === 'open' ? 'Best fit' : 'Your choice', primary);
      addShapeCard('Alternative', alternative);
    }
  };

  VirrelliTableCalculator.prototype.renderProducts = function (primary) {
    var s = this.state;
    var self = this;
    var wrap = this.querySelector('[data-calc-products]');
    var bespoke = this.querySelector('[data-calc-bespoke]');
    var bespokeDims = this.querySelector('[data-calc-bespoke-dimensions]');
    if (!wrap) return;

    // A product qualifies if it seats the request, fits the room, and
    // maintains at least minimum clearance.
    var matches = this.products.map(function (p) {
      var fits = self.productFit(p);
      return fits ? { p: p, fit: fits.fit, closeness: fits.closeness } : null;
    }).filter(Boolean);

    // Ranking: preferred shape, then comfortable clearance, then closeness
    // to the recommended size, then block order.
    matches.sort(function (a, b) {
      var prefA = (s.shape !== 'open' && a.p.shape === s.shape) ? 0 : (a.p.shape === primary.shape ? 1 : 2);
      var prefB = (s.shape !== 'open' && b.p.shape === s.shape) ? 0 : (b.p.shape === primary.shape ? 1 : 2);
      if (prefA !== prefB) return prefA - prefB;
      var f = fitRank(a.fit) - fitRank(b.fit);
      if (f !== 0) return f;
      if (a.closeness !== b.closeness) return a.closeness - b.closeness;
      return a.p.order - b.p.order;
    });

    var top = matches.slice(0, 3);

    // Show/hide the server-rendered cards
    this.products.forEach(function (p) { p.el.hidden = true; });
    top.forEach(function (m) { m.p.el.hidden = false; });

    var productsBlock = this.querySelector('[data-calc-products-block]');
    if (productsBlock) productsBlock.hidden = top.length === 0;

    // Bespoke: always present as secondary CTA; primary when nothing fits well
    if (bespoke) {
      var noGoodMatch = top.length === 0 || primary.fit === FIT.BESPOKE;
      bespoke.setAttribute('data-emphasis', noGoodMatch ? 'primary' : 'secondary');
      if (bespokeDims) {
        bespokeDims.textContent = primary.fit === FIT.BESPOKE
          ? 'Based on your room, we\u2019d be looking at a ' + shapeLabel(primary.shape).toLowerCase() + ' table sized around your space for ' + s.seats + ' people.'
          : 'As a guide, we\u2019d suggest a ' + primary.shape + ' table around ' + sizeText(primary) + ' for ' + s.seats + ' people.';
      }
    }
  };

  /**
   * Does a single product fit this room + seating request?
   * Returns { fit, closeness } or null.
   */
  VirrelliTableCalculator.prototype.productFit = function (p) {
    var s = this.state;
    if (s.seats < p.minSeats || s.seats > p.maxSeats) return null;

    var comfy = this.clearComfy, minimum = this.clearMin;
    var guidance = guidanceFor(p.shape === 'round' ? 'round' : p.shape, s.seats);

    if (p.shape === 'round') {
      var smallest = Math.min(s.roomL, s.roomW);
      if (p.diameter + 2 * minimum > smallest) return null;
      var fit = (p.diameter + 2 * comfy <= smallest) ? FIT.COMFORTABLE : FIT.POSSIBLE;
      var target = (guidance.d[0] + guidance.d[1]) / 2;
      return { fit: fit, closeness: Math.abs(p.diameter - target) };
    }

    // Oval / rectangular: either orientation counts
    var fitsOrientation = function (L, W) {
      if (p.length + 2 * minimum > L || p.width + 2 * minimum > W) return null;
      return (p.length + 2 * comfy <= L && p.width + 2 * comfy <= W) ? FIT.COMFORTABLE : FIT.POSSIBLE;
    };
    var a = fitsOrientation(s.roomL, s.roomW);
    var b = fitsOrientation(s.roomW, s.roomL);
    if (!a && !b) return null;
    var best = (a && b) ? (fitRank(a) <= fitRank(b) ? a : b) : (a || b);
    var targetL = (guidance.l[0] + guidance.l[1]) / 2;
    return { fit: best, closeness: Math.abs(p.length - targetL) };
  };

  /* ---------------- enquiry + analytics ---------------- */

  VirrelliTableCalculator.prototype.enquirySummary = function () {
    var s = this.state, r = this.lastResult;
    if (!r) return '';
    var lines = [
      'Dining Table Size Calculator summary',
      'Room: ' + (s.roomL / 1000) + 'm x ' + (s.roomW / 1000) + 'm',
      'Seating: ' + s.seats + ' people',
      'Preferred shape: ' + (s.shape === 'open' ? 'Open to suggestions' : shapeLabel(s.shape)),
      'Recommended: ' + shapeLabel(r.primary.shape) + ' ' + sizeText(r.primary),
      'Fit status: ' + r.primary.fit
    ];
    var visible = this.products.filter(function (p) { return !p.el.hidden; });
    if (visible.length) {
      lines.push('Suggested Virrelli tables: ' + visible.map(function (p) {
        var t = p.el.querySelector('[data-calc-product-title]');
        return t ? t.textContent.trim() : '';
      }).filter(Boolean).join('; '));
    }
    return lines.join('\n');
  };

  VirrelliTableCalculator.prototype.track = function (eventName, data) {
    var payload = Object.assign({ event: eventName }, data);
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push(payload);
    }
    document.dispatchEvent(new CustomEvent(eventName, { detail: data, bubbles: true }));
  };

  customElements.define('virrelli-table-calculator', VirrelliTableCalculator);

  // Theme Editor: sections are re-rendered on setting changes. The custom
  // element re-initialises via connectedCallback; clear the flag on unload.
  document.addEventListener('shopify:section:unload', function (e) {
    var calc = e.target.querySelector && e.target.querySelector('virrelli-table-calculator');
    if (calc) calc.dataset.initialised = 'false';
  });
})();
